import {httpsCallable} from "firebase/functions";
import firebase from "../core/firebase";
import {
  AI_CREDIT_COSTS,
  ensureAiCredits,
  notifyInsufficientAiCredits,
} from "./ai-credit-guard";

const ensureAiResponse = <T>(
  data: { code?: number; error?: string; data?: T },
  fallbackMessage: string,
  cost: number
): T => {
  if (data.code === 402 || data.error === "INSUFFICIENT_AI_CREDITS") {
    notifyInsufficientAiCredits(cost);
    throw new Error("Créditos de IA insuficientes.");
  }
  if (data.code !== 200 || !data.data) {
    throw new Error(data.error || fallbackMessage);
  }
  return data.data;
};

export type ExtractProductAiResult = {
  name: string;
  price?: number | null;
  suggestedCategoryId?: string | null;
  suggestedCategoryLabel?: string | null;
  notes?: string;
};

export async function aiExtractProductFromImage(payload: {
  imageBase64: string;
  mimeType: string;
  categories?: Array<{id: string; name: string}>;
}): Promise<ExtractProductAiResult> {
  await ensureAiCredits(AI_CREDIT_COSTS.extractProductFromImage);
  const call = httpsCallable(firebase.functions, "aiExtractProductFromImage");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: ExtractProductAiResult;
  };
  return ensureAiResponse(data, "Não foi possível analisar a imagem.", AI_CREDIT_COSTS.extractProductFromImage);
}

export type SellCommandAiResult = {
  assistantText: string;
  suggestedProductIds: string[];
};

export type EnhanceProductImageAiResult = {
  imageBase64: string;
  mimeType: string;
};

export async function aiEnhanceProductImage(payload: {
  /** Caminho do objeto no bucket (ex.: `{negócio}/products/{id}.png`). Evita CORS no navegador. */
  storageObjectPath: string;
  productName?: string;
}): Promise<EnhanceProductImageAiResult> {
  await ensureAiCredits(AI_CREDIT_COSTS.enhanceProductImage);
  const call = httpsCallable(firebase.functions, "aiEnhanceProductImage");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: EnhanceProductImageAiResult;
  };
  return ensureAiResponse(data, "Não foi possível melhorar a imagem.", AI_CREDIT_COSTS.enhanceProductImage);
}

export type ExtractPayableAiResult = {
  title: string;
  amount?: number | null;
  dueDate: string;
  payeeName?: string | null;
  reference?: string | null;
  notes?: string;
};

export type BulkProductAiRow = {
  name: string;
  code?: string | null;
  price?: number | null;
  stock?: number | null;
  categoryName?: string | null;
  supplierName?: string | null;
  categoryExplicit?: boolean | null;
  supplierExplicit?: boolean | null;
};

export type BulkProductsExtractResult = {
  products: BulkProductAiRow[];
};

export async function aiBulkProductsExtract(payload: {
  textContent?: string;
  mediaBase64?: string;
  mimeType?: string;
  categoryNamesHint?: string;
  supplierNamesHint?: string;
}): Promise<BulkProductsExtractResult> {
  await ensureAiCredits(AI_CREDIT_COSTS.bulkProductsExtract);
  const call = httpsCallable(firebase.functions, "aiBulkProductsExtract");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: BulkProductsExtractResult;
  };
  return ensureAiResponse(data, "Não foi possível extrair produtos.", AI_CREDIT_COSTS.bulkProductsExtract);
}

export async function aiExtractPayableFromDocument(payload: {
  documentBase64: string;
  mimeType: string;
}): Promise<ExtractPayableAiResult> {
  await ensureAiCredits(AI_CREDIT_COSTS.extractPayableFromDocument);
  const call = httpsCallable(firebase.functions, "aiExtractPayableFromDocument");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: ExtractPayableAiResult;
  };
  return ensureAiResponse(data, "Não foi possível ler o documento.", AI_CREDIT_COSTS.extractPayableFromDocument);
}

export async function aiSellCommand(payload: {
  sellId: string;
  userMessage: string;
  history?: Array<{role: "user" | "model"; content: string}>;
}): Promise<SellCommandAiResult> {
  await ensureAiCredits(AI_CREDIT_COSTS.sellCommand);
  const call = httpsCallable(firebase.functions, "aiSellCommand");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: SellCommandAiResult;
  };
  return ensureAiResponse(data, "Não foi possível processar o comando.", AI_CREDIT_COSTS.sellCommand);
}

export type CopilotInsightCard = {
  title: string;
  subtitle: string;
  productId: string;
};

export type CopilotQuickAction = {
  title: string;
  pdvAction:
    | "apply_discount"
    | "mark_as_credit"
    | "remove_last_item"
    | "clear_cart"
    | "duplicate_last_item"
    | "round_total";
  discountPercent?: number | null;
  roundTarget?: number | null;
  confirm: boolean;
};

export type CopilotChatBlock = {
  kind: "info" | "suggestion" | "action";
  title: string;
  infoValue?: string | null;
  highlight?: boolean;
  subtitle?: string | null;
  productId?: string | null;
  pdvAction?: string | null;
  discountPercent?: number | null;
  confirm?: boolean;
  newProductName?: string | null;
  newProductPrice?: number | null;
};

export type SellCopilotResult = {
  insights: CopilotInsightCard[];
  quickActions: CopilotQuickAction[];
  chatBlocks: CopilotChatBlock[];
};

export async function aiSellCopilot(payload: {
  sellId: string;
  userMessage?: string;
}): Promise<SellCopilotResult> {
  const call = httpsCallable(firebase.functions, "aiSellCopilot");
  const res = await call(payload);
  const data = res.data as {
    code?: number;
    error?: string;
    data?: SellCopilotResult;
  };
  if (data.code !== 200 || !data.data) {
    throw new Error(data.error || "Não foi possível gerar sugestões.");
  }
  return data.data;
}
